import { useState, useCallback } from 'react';
import { Download } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import StatsCards from '../components/towns/StatsCards';
import SearchFilterBar from '../components/towns/SearchFilterBar';
import TownTable from '../components/towns/TownTable';
import Pagination from '../components/towns/Pagination';
import TownDetailPanel from '../components/towns/TownDetailPanel';
import { useTowns } from '../hooks/useTowns';
import api from '../lib/api';

const DEFAULT_LIMIT = 20;

export default function TownListPage() {
    // 검색/필터 상태
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [orderer, setOrderer] = useState('');
    const [contractType, setContractType] = useState('');
    const [settingStaff, setSettingStaff] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [onepass, setOnepass] = useState('');
    const [emergencyBell, setEmergencyBell] = useState('');
    const [expiringFilter, setExpiringFilter] = useState(false);
    const [recentFilter, setRecentFilter] = useState(false);
    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);

    // 상세 패널용 선택 상태
    const [selectedTownId, setSelectedTownId] = useState<number | null>(null);

    const { data, isLoading } = useTowns({
        page,
        limit: DEFAULT_LIMIT,
        search: search || undefined,
        category: category || undefined,
        orderer: orderer || undefined,
        contractType: contractType || undefined,
        settingStaff: settingStaff || undefined,
        status: statusFilter || undefined,
        onepass: onepass || undefined,
        emergencyBell: emergencyBell || undefined,
        expiring: expiringFilter || undefined,
        recent: recentFilter || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortBy ? sortOrder : undefined,
    });

    const towns = data?.data ?? [];
    const pagination = data?.pagination ?? { page: 1, limit: DEFAULT_LIMIT, total: 0, totalPages: 1 };

    const handleReset = useCallback(() => {
        setSearch('');
        setCategory('');
        setOrderer('');
        setContractType('');
        setSettingStaff('');
        setStatusFilter('');
        setOnepass('');
        setEmergencyBell('');
        setExpiringFilter(false);
        setSortBy('');
        setSortOrder('asc');
        setPage(1);
    }, []);

    const handleSearchChange = useCallback((v: string) => { setSearch(v); setPage(1); }, []);
    const handleCategoryChange = useCallback((v: string) => { setCategory(v); setOrderer(''); setContractType(''); setPage(1); }, []);
    const handleOrdererChange = useCallback((v: string) => { setOrderer(v); setPage(1); }, []);
    const handleContractTypeChange = useCallback((v: string) => { setContractType(v); setPage(1); }, []);
    const handleSettingStaffChange = useCallback((v: string) => { setSettingStaff(v); setPage(1); }, []);
    const handleStatusFilterChange = useCallback((v: string) => { setStatusFilter(v); setPage(1); }, []);
    const handleOnepassChange = useCallback((v: string) => { setOnepass(v); setPage(1); }, []);
    const handleEmergencyBellChange = useCallback((v: string) => { setEmergencyBell(v); setPage(1); }, []);

    const handleExpiringToggle = useCallback(() => {
        setExpiringFilter((prev) => !prev);
        setPage(1);
    }, []);

    const [onepassCardFilter, setOnepassCardFilter] = useState<string>('');

    const handleOnepassAllClick = useCallback(() => {
        if (onepassCardFilter === 'all') {
            setOnepassCardFilter('');
            setOnepass('');
        } else {
            setOnepassCardFilter('all');
            setOnepass('마이타운,자이패스,오이패스,노크타운,스마트원,아파트너,아파트스토리,스마트패스,코콤');
        }
        setPage(1);
    }, [onepassCardFilter]);

    const handleOnepassNoXiClick = useCallback(() => {
        if (onepassCardFilter === 'noxi') {
            setOnepassCardFilter('');
            setOnepass('');
        } else {
            setOnepassCardFilter('noxi');
            setOnepass('마이타운,오이패스,노크타운,스마트원,아파트너,아파트스토리,스마트패스,코콤');
        }
        setPage(1);
    }, [onepassCardFilter]);

    const handleRecentClick = useCallback(() => {
        setRecentFilter((prev) => !prev);
        setPage(1);
    }, []);

    const handleSort = useCallback((column: string) => {
        setSortBy((prev) => {
            if (prev === column) {
                setSortOrder((o) => o === 'asc' ? 'desc' : 'asc');
                return column;
            }
            setSortOrder('asc');
            return column;
        });
        setPage(1);
    }, []);

    const handleClosePanel = useCallback(() => {
        setSelectedTownId(null);
    }, []);

    const handleAddTown = useCallback(async () => {
        const name = window.prompt('추가할 단지명을 입력하세요:');
        if (!name || !name.trim()) return;
        try {
            const { data } = await api.post('/towns', { name: name.trim() });
            setSelectedTownId(data.id);
        } catch (err: any) {
            alert(err.response?.data?.error || '단지 추가에 실패했습니다');
        }
    }, []);

    // 엑셀 내보내기
    const handleExport = useCallback(async () => {
        try {
            const params: Record<string, string> = {};
            if (search) params.search = search;
            if (category) params.category = category;
            if (orderer) params.orderer = orderer;
            if (contractType) params.contractType = contractType;
            if (settingStaff) params.settingStaff = settingStaff;
            if (statusFilter) params.status = statusFilter;
            if (onepass) params.onepass = onepass;
            if (emergencyBell) params.emergencyBell = emergencyBell;
            if (expiringFilter) params.expiring = 'true';

            const response = await api.get('/export/towns', {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = '단지목록.xlsx';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i);
                if (match?.[1]) {
                    filename = decodeURIComponent(match[1].replace(/"/g, ''));
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            console.error('엑셀 내보내기 실패');
        }
    }, [search, category, orderer, contractType, settingStaff, statusFilter, onepass, emergencyBell, expiringFilter]);

    return (
        <Layout>
            <Header
                title="단지 목록"
                subtitle="전체 단지 정보를 관리합니다"
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExport}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        >
                            <Download className="w-3.5 h-3.5" />
                            엑셀 내보내기
                        </button>
                        <button
                            onClick={handleAddTown}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium bg-[#1ABB9C] text-white rounded-lg hover:bg-[#169F85] transition"
                        >
                            + 단지 추가
                        </button>
                    </div>
                }
            />

            <div className="px-8 py-6">
                <StatsCards
                    expiringActive={expiringFilter}
                    onepassFilter={onepassCardFilter}
                    onExpiringClick={handleExpiringToggle}
                    onRecentClick={handleRecentClick}
                    recentActive={recentFilter}
                    onOnepassAllClick={handleOnepassAllClick}
                    onOnepassNoXiClick={handleOnepassNoXiClick}
                />

                <SearchFilterBar
                    search={search}
                    category={category}
                    orderer={orderer}
                    contractType={contractType}
                    settingStaff={settingStaff}
                    statusFilter={statusFilter}
                    onepass={onepass}
                    emergencyBell={emergencyBell}
                    onSearchChange={handleSearchChange}
                    onCategoryChange={handleCategoryChange}
                    onOrdererChange={handleOrdererChange}
                    onContractTypeChange={handleContractTypeChange}
                    onSettingStaffChange={handleSettingStaffChange}
                    onStatusFilterChange={handleStatusFilterChange}
                    onOnepassChange={handleOnepassChange}
                    onEmergencyBellChange={handleEmergencyBellChange}
                    onReset={handleReset}
                />

                {/* 검색 결과 요약 바 */}
                {data?.summary && (
                    <div className="flex items-center gap-5 px-4 py-2.5 mb-4 bg-[#F0F4F7] rounded border border-[#E6E9ED] text-sm text-[#73879C]">
                        <span>
                            검색 결과 <span className="font-semibold text-[#2A3F54]">{pagination.total.toLocaleString()}</span>개 단지
                        </span>
                        <span className="text-[#D5D5D5]">|</span>
                        <span>
                            세대수 <span className="font-semibold text-[#2A3F54]">{data.summary.totalHouseholds.toLocaleString()}</span>
                        </span>
                        <span className="text-[#D5D5D5]">|</span>
                        <span>
                            사용세대 <span className="font-semibold text-[#2A3F54]">{data.summary.totalActiveHouseholds.toLocaleString()}</span>
                        </span>
                        <span className="text-[#D5D5D5]">|</span>
                        <span>
                            사용자 <span className="font-semibold text-[#2A3F54]">{data.summary.totalActiveUsers.toLocaleString()}</span>
                        </span>
                    </div>
                )}

                <TownTable
                    towns={towns}
                    isLoading={isLoading}
                    selectedTownId={selectedTownId}
                    onSelectTown={setSelectedTownId}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                />

                <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={setPage}
                />
            </div>

            {/* 상세 패널 */}
            {selectedTownId !== null && (
                <TownDetailPanel
                    townId={selectedTownId}
                    onClose={handleClosePanel}
                />
            )}
        </Layout>
    );
}
